import { useEffect, useRef, useState } from "react";
import { Sheet, SegmentedControl, IOSButton, Field } from "@/components/ios";
import { Eraser } from "lucide-react";

export default function SignatureSheet({ open, onClose, onSubmit, busy }) {
  const [mode, setMode] = useState("draw");
  const [name, setName] = useState("");
  const [hasInk, setHasInk] = useState(false);
  const [fontReady, setFontReady] = useState(false);
  const canvasRef = useRef(null);
  const drawingRef = useRef(false);

  useEffect(() => {
    if (open && document.fonts?.load) {
      document.fonts.load('600 64px Caveat').then(() => setFontReady(true)).catch(() => {});
    }
  }, [open]);

  useEffect(() => {
    if (!open) return;
    const c = canvasRef.current;
    if (!c) return;
    c.width = c.offsetWidth * 2;
    c.height = 360;
    const ctx = c.getContext("2d");
    ctx.lineWidth = 5;
    ctx.lineCap = "round";
    ctx.lineJoin = "round";
    ctx.strokeStyle = "#0A0A0A";
    setHasInk(false);
    setName("");
  }, [open]);

  useEffect(() => {
    if (mode !== "type") return;
    const c = canvasRef.current;
    if (!c) return;
    const ctx = c.getContext("2d");
    ctx.clearRect(0, 0, c.width, c.height);
    const text = name.trim();
    if (!text) {
      setHasInk(false);
      return;
    }
    let size = 120;
    ctx.font = `600 ${size}px Caveat, 'Segoe Script', 'Brush Script MT', cursive`;
    while (ctx.measureText(text).width > c.width - 80 && size > 32) {
      size -= 8;
      ctx.font = `600 ${size}px Caveat, 'Segoe Script', 'Brush Script MT', cursive`;
    }
    ctx.fillStyle = "#0A0A0A";
    ctx.textBaseline = "middle";
    ctx.fillText(text, 40, c.height / 2);
    setHasInk(true);
  }, [mode, name, fontReady, open]);

  useEffect(() => {
    if (!open || !document.fonts?.ready) return;
    let alive = true;
    document.fonts.ready.then(() => alive && setFontReady((r) => !r));
    return () => {
      alive = false;
    };
  }, [open]);

  const ctxOf = () => canvasRef.current?.getContext("2d");

  const pos = (e) => {
    const c = canvasRef.current;
    const r = c.getBoundingClientRect();
    return [(e.clientX - r.left) * (c.width / r.width), (e.clientY - r.top) * (c.height / r.height)];
  };

  const startDraw = (e) => {
    if (mode !== "draw") return;
    drawingRef.current = true;
    const ctx = ctxOf();
    const [x, y] = pos(e);
    ctx.beginPath();
    ctx.moveTo(x, y);
    e.currentTarget.setPointerCapture?.(e.pointerId);
  };

  const moveDraw = (e) => {
    if (!drawingRef.current || mode !== "draw") return;
    const ctx = ctxOf();
    const [x, y] = pos(e);
    ctx.lineTo(x, y);
    ctx.stroke();
    if (!hasInk) setHasInk(true);
  };

  const endDraw = () => {
    drawingRef.current = false;
  };

  const clear = () => {
    const c = canvasRef.current;
    if (c) c.getContext("2d").clearRect(0, 0, c.width, c.height);
    setHasInk(false);
  };

  const submit = () => {
    const c = canvasRef.current;
    if (!c) return;
    onSubmit({
      mode: mode === "draw" ? "drawn" : "typed",
      name: name.trim(),
      signature_b64: c.toDataURL("image/png"),
    });
  };

  return (
    <Sheet open={open} onClose={onClose} title="Add your signature" testid="signature-sheet">
      <div className="space-y-5">
        <SegmentedControl
          testid="signature-mode"
          value={mode}
          onChange={setMode}
          options={[
            { value: "draw", label: "Draw" },
            { value: "type", label: "Type" },
          ]}
        />
        <Field
          label="Full legal name"
          testid="signature-name-input"
          value={name}
          onChange={(e) => setName(e.target.value)}
          placeholder="Jane Founder"
          maxLength={80}
        />
        <div className="relative">
          <canvas
            ref={canvasRef}
            data-testid="signature-canvas"
            onPointerDown={startDraw}
            onPointerMove={moveDraw}
            onPointerUp={endDraw}
            onPointerLeave={endDraw}
            className={`w-full h-[180px] bg-[#F2F2F7] rounded-sm border border-black/[0.05] touch-none ${
              mode === "draw" ? "cursor-crosshair" : "cursor-default"
            }`}
          />
          {mode === "draw" && (
            <button
              type="button"
              onClick={clear}
              data-testid="signature-clear-button"
              className="absolute top-3 right-3 w-9 h-9 rounded-full bg-white shadow-sm flex items-center justify-center active:scale-95 transition-transform duration-150"
              aria-label="Clear signature"
            >
              <Eraser className="w-4 h-4 text-[#3C3C43]/70" strokeWidth={1.5} />
            </button>
          )}
          {mode === "type" && !name.trim() && (
            <span className="absolute inset-0 flex items-center justify-center text-[15px] text-[#3C3C43]/35 pointer-events-none">
              Type your name above to preview
            </span>
          )}
        </div>
        <p className="text-[13px] leading-relaxed text-[#3C3C43]/60">
          By signing, you agree this electronic signature is legally binding. A timestamp and device record are
          stored with the document's audit trail.
        </p>
        <IOSButton
          data-testid="signature-submit-button"
          onClick={submit}
          disabled={!name.trim() || !hasInk || busy}
          className="w-full"
        >
          {busy ? "Signing…" : "Sign document"}
        </IOSButton>
      </div>
    </Sheet>
  );
}
